import {
  AddMultiDocumentArgs,
  MultiDocumentDownloadUrlArgs,
  MultiDocumentsArgs,
  OnboardingMultiDocument,
  OnboardingMultiDocumentsResponse,
  OnboardingStoreType,
  OracleOnboardingMultiDocumentSectionKey,
  RemoveMultiDocumentArgs,
  RemoveMultiDocumentResponse,
  SourceDownloadUrlResponse,
  UpdateMultiDocumentArgs
} from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { awaitJSON, OracleLambdaFetch } from '@src/utils/fetch-utils';
import { uploadToPresignedUrl } from './onboarding-api';

export const fetchMultiDocuments = async ({
  company,
  store,
  sectionKey
}: MultiDocumentsArgs): Promise<OnboardingMultiDocumentsResponse> => {
  const res = await OracleLambdaFetch('/onboarding/multi-document', {
    params: { company, store, sectionKey }
  });
  return awaitJSON<OnboardingMultiDocumentsResponse>(res);
};

interface UploadMultiDocumentFileArgs {
  company: string;
  store: OnboardingStoreType;
  sectionKey: OracleOnboardingMultiDocumentSectionKey;
  file: File;
  // Present when the file replaces this document's file.
  documentId?: number;
}

interface UploadedMultiDocumentFile {
  fileId: string;
  fileName: string;
}

// The caller then adds a document, or updates one, with the uploaded file.
export const uploadMultiDocumentFile = async ({
  company,
  store,
  sectionKey,
  file,
  documentId
}: UploadMultiDocumentFileArgs): Promise<UploadedMultiDocumentFile> => {
  const fileId = await uploadToPresignedUrl(
    '/onboarding/multi-document/upload-url',
    {
      company,
      store,
      sectionKey,
      contentType: file.type,
      fileName: file.name,
      sizeBytes: file.size,
      ...(documentId !== undefined ? { documentId } : {})
    },
    file
  );
  return { fileId, fileName: file.name };
};

export const addMultiDocument = async (
  body: AddMultiDocumentArgs
): Promise<OnboardingMultiDocument> => {
  const res = await OracleLambdaFetch('/onboarding/multi-document', { method: 'post', body });
  return awaitJSON<OnboardingMultiDocument>(res);
};

export const updateMultiDocument = async (
  body: UpdateMultiDocumentArgs
): Promise<OnboardingMultiDocument> => {
  const res = await OracleLambdaFetch('/onboarding/multi-document/update', {
    method: 'post',
    body
  });
  return awaitJSON<OnboardingMultiDocument>(res);
};

export const removeMultiDocument = async (body: RemoveMultiDocumentArgs): Promise<void> => {
  const res = await OracleLambdaFetch('/onboarding/multi-document/remove', {
    method: 'post',
    body
  });
  await awaitJSON<RemoveMultiDocumentResponse>(res);
};

// Call on click, not on render: a URL minted at render time may expire before anyone clicks.
export const fetchMultiDocumentDownloadUrl = async ({
  company,
  store,
  documentId
}: MultiDocumentDownloadUrlArgs): Promise<string> => {
  const res = await OracleLambdaFetch('/onboarding/multi-document/download', {
    params: { company, store, documentId: String(documentId) }
  });
  const { url } = await awaitJSON<SourceDownloadUrlResponse>(res);
  return url;
};
